import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { isEmpty, map, some, transform, upperCase } from "lodash";
import { FiDollarSign, FiDownload } from "react-icons/fi";

import { getPaginatedPayrolls } from "actions/adminActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { formatCurrency } from "utils/number";
import Alert from "components/Alert";
import Button from "components/Button";
import CopyIcon from "components/CopyIcon";
import Paginator from "components/Paginator";
import SimpleModal from "components/SimpleModal";
import InfoTable from "components/InfoTable";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconSpinner from "components/IconSpinner";
import IconMessageCard from "components/IconMessageCard";
import IconTableHeader from "components/IconTableHeader";
import { Col, Form, Row } from "react-bootstrap";
import { adminConstants } from "actions/types";
import { getEmployerDownloadUrl } from "services/documentService";
import { simulateLinkClickNewTab } from "utils/dom";
import { wrapError } from "utils/errorHelper";
import type { RootState } from "types/state";
import type { GetPayrollsQueryVariables } from "types/graphql";
import PayrollEmployerGroup from "./PayrollEmployerGroup";
import PayrollErrorReason, {
  PAYROLL_IN_ERROR_STATE,
} from "./PayrollErrorReason";

const PENDING_DOCUMENT_ID = "-1";

/** Shallow shape of a payroll row as the column renderers read it. */
interface PayrollRow {
  id?: string;
  employerId: string;
  state?: string;
  status?: string;
  period: { displayStart?: string; displayEnd?: string };
  metadata: Record<string, unknown> & {
    processingDate?: string;
    payrollType?: string;
    errorReason?: string | null;
    errorDetail?: string | null;
  };
  totalAmount?: number | string;
  payrollDocumentId: string;
  employerGroup?: { id: string; name?: string | null } | null;
}

const isLoadingSelector = createLoadingSelector(adminConstants.GET_PAYROLLS);
const errorSelector = createErrorSelector(adminConstants.GET_PAYROLLS);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isLoadingSelector(state),
    payrolls: state.admin.payrolls,
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  getPaginatedPayrolls,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
  /** declared in the original PropTypes but never read in this component */
  payrollsCount?: number;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface PayrollFilters {
  status: string | null;
  order: string;
  companyId?: string;
}

interface State {
  initialFetching: boolean;
  metadata: Record<string, unknown>;
  page: number;
  limit: number;
  offset: number;
  totalRows: number;
  payrolls: PayrollRow[];
  filters: PayrollFilters;
  // written by setState below; absent from initial state
  fetching?: boolean;
  error?: string;
  showMetadataModal?: boolean;
}

class Payrolls extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    let filters: PayrollFilters = {
      status: null,
      order: "DESC",
    };
    if (!isEmpty(props.companyId)) {
      filters = {
        ...filters,
        companyId: props.companyId,
      };
    }

    this.state = {
      initialFetching: true,
      metadata: {},
      page: 0,
      limit: 25,
      offset: 0,
      totalRows: 0,
      payrolls: [],
      filters,
    };
  }

  componentDidMount() {
    this._getPaginatedPayrolls();
  }

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () =>
      this._getPaginatedPayrolls()
    );
  };

  _getPaginatedPayrolls = () => {
    this.setState({ fetching: true }, () => {
      this.props
        // filters hold loose strings at runtime; the params type narrows
        // status/order to the PayrollStatus/Order literal unions
        .getPaginatedPayrolls(
          this.props.client as ApolloClient<object>,
          {
            limit: this.state.limit,
            offset: this.state.offset,
            ...this.state.filters,
          } as GetPayrollsQueryVariables
        )
        .then(() => {
          this.setState({
            initialFetching: false,
          });
        })
        .catch((ex: { message?: string }) => {
          this.setState({
            fetching: false,
            initialFetching: false,
            error: ex.message,
          });
        });
    });
  };

  _buildPayrollColumns = (
    payrolls: PayrollRow[]
  ): Array<IconTableColumn<PayrollRow>> => {
    const hasErroredPayroll = some(
      payrolls,
      (payroll) => payroll.state === PAYROLL_IN_ERROR_STATE
    );
    const columns: Array<IconTableColumn<PayrollRow>> = [
      {
        label: "Payroll ID",
        key: "id",
        customComponent: (props: PayrollRow) => (
          <>
            {props.id}
            <CopyIcon copyText={props.id} label="Payroll ID" />
          </>
        ),
      },
      ...(isEmpty(this.props.companyId)
        ? [{ label: "Employer Id", key: "employerId" }]
        : []),
      {
        label: "Payroll State",
        key: "state",
      },
      {
        label: "Payroll Status",
        key: "status",
      },
      ...(hasErroredPayroll
        ? [
            {
              label: "Error Reason",
              customComponent: (props: PayrollRow) => {
                return (
                  <div>
                    <PayrollErrorReason
                      state={props.state}
                      errorReason={props.metadata.errorReason}
                      errorDetail={props.metadata.errorDetail}
                    />
                  </div>
                );
              },
            },
          ]
        : []),
      {
        label: "Pay Period",
        customComponent: (props: PayrollRow) => {
          return (
            <div>
              {props.period.displayStart} - {props.period.displayEnd}
            </div>
          );
        },
      },
      {
        label: "Processing Date",
        customComponent: (props: PayrollRow) => {
          return <div>{props.metadata.processingDate}</div>;
        },
      },
      {
        label: "Type",
        customComponent: (props: PayrollRow) => {
          return <div>{props.metadata.payrollType}</div>;
        },
      },
      {
        label: "Employer Group",
        customComponent: (props: PayrollRow) => {
          return (
            <div>
              <PayrollEmployerGroup employerGroup={props.employerGroup} />
            </div>
          );
        },
      },
      {
        label: "Payroll Amount",
        customComponent: (props: PayrollRow) => {
          return <div>{formatCurrency(props.totalAmount as number)}</div>;
        },
      },

      {
        label: "Metadata",
        customComponent: (props: PayrollRow) => {
          return (
            <div>
              <Button
                color="action"
                name="action"
                btnLabel="View Metadata"
                onClick={() => {
                  this.setState({
                    showMetadataModal: true,
                    metadata: props.metadata,
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        label: "Action",
        customComponent: (props: PayrollRow) => (
          <>
            {props.payrollDocumentId === PENDING_DOCUMENT_ID ? null : (
              <a
                className="link"
                target="_blank"
                onClick={() =>
                  this._downloadFile(props.payrollDocumentId, props.employerId)
                }
              >
                <FiDownload
                  fill={"#FFFFFF"}
                  color={"#FFFFFF"}
                  stroke={"#29b0c2"}
                  size={"20px"}
                />
              </a>
            )}
          </>
        ),
      },
    ];
    return columns;
  };

  _downloadFile(documentId: string, companyId: string) {
    getEmployerDownloadUrl(this.props.client as ApolloClient<object>, {
      documentId,
      companyId,
    })
      .then((response) => {
        // found-not-fixed: query result is `{ url } | null`; original reads
        // response.url unconditionally and throws if the query returns null
        simulateLinkClickNewTab(response!.url);
      })
      .catch((error: unknown) => {
        this.setState({ error: wrapError(error) });
      });
  }

  _handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let status: string | null = e.target.value;
    if (status === "ALL") {
      status = null;
    }
    this.setState(
      {
        filters: { ...this.state.filters, status },
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedPayrolls()
    );
  };

  _handleOrderFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState(
      {
        filters: { ...this.state.filters, order: e.target.value },
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedPayrolls()
    );
  };

  _buildPayrollStatuses = () => {
    return [
      // null value returns all payrolls
      {
        label: "All",
        value: "ALL",
      },
      {
        label: "Complete",
        value: "COMPLETE",
      },
      {
        label: "Processing",
        value: "PROCESSING",
      },
      {
        label: "Error",
        value: "ERROR",
      },
      {
        label: "Canceled",
        value: "CANCELED",
      },
      {
        label: "Approved",
        value: "APPROVED",
      },
      {
        label: "Waiting for Action",
        value: "WAITING_FOR_ACTION",
      },
    ];
  };

  _buildPayrollOrder = () => {
    return [
      { label: "Desc", value: "DESC" },
      { label: "Asc", value: "ASC" },
    ];
  };

  _buildMetadataTable = () => {
    const metadata = transform(
      this.state.metadata,
      (acc: Array<{ key: string; label: string }>, val, key) => {
        const labelObject = {
          key,
          label: upperCase(key),
        };
        acc.push(labelObject);
        return acc;
      },
      [] as Array<{ key: string; label: string }>
    );
    return <InfoTable metadata={metadata} data={this.state.metadata} />;
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    if (this.props.error) {
      return <Alert type={"error"} msg={this.props.error} />;
    }

    // admin slice models results as unknown[]; this page reads payroll rows
    const data = this.props.payrolls.results as PayrollRow[];
    const columns = this._buildPayrollColumns(data);
    const showBlankSlate = isEmpty(data);

    return (
      <div className="company-payrolls">
        <Row>
          <Col md={4}>
            <div
              style={{ paddingLeft: 15, paddingBottom: 15 }}
              className="state-filter"
            >
              <Form.Label>Filter by status</Form.Label>
              <Form.Select
                name="statusFilter"
                // filters.status is null initially ("ALL"); the native select
                // value prop is typed string|number|string[] — pass it through
                value={this.state.filters.status as string | undefined}
                onChange={this._handleStatusFilterChange}
              >
                {map(this._buildPayrollStatuses(), (filter) => (
                  <option key={filter.label} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>
          <Col md={4}>
            <div
              style={{ paddingLeft: 15, paddingBottom: 15 }}
              className="state-filter"
            >
              <Form.Label>Order</Form.Label>
              <Form.Select
                name="orderFilter"
                value={this.state.filters.order}
                onChange={this._handleOrderFilterChange}
              >
                {map(this._buildPayrollOrder(), (filter) => (
                  <option key={filter.label} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>
        </Row>

        {showBlankSlate && (
          <div>
            <IconMessageCard
              header={"No Payrolls"}
              subheader="There are currently no payrolls to display."
              icon={<FiDollarSign color="white" stroke="#60A4BF" size={16} />}
            />
          </div>
        )}
        {this.props.isFetching && <IconSpinner centered />}
        {!showBlankSlate && (
          <>
            {this.state.showMetadataModal && (
              <SimpleModal
                show={true}
                size={"lg"}
                onClose={() =>
                  this.setState({ showMetadataModal: false, metadata: {} })
                }
                title="Payroll Metadata"
                buttonText="close"
              >
                {this._buildMetadataTable()}
              </SimpleModal>
            )}

            <div className="action-box">
              <IconTableHeader
                tableHeader="Payrolls"
                tableCount={this.props.payrolls.totalRows}
              />
              <IconTable columns={columns} data={data} />
              <Paginator
                onChange={this._onPageChange}
                pageCount={this.props.payrolls.totalRows / this.state.limit}
                page={this.state.page}
              />
            </div>
          </>
        )}
      </div>
    );
  }
}

export default connector(withApollo<Props>(Payrolls));
